import { useState, useEffect, useCallback, useRef } from 'react';
import { X, ChevronLeft, ChevronRight, Play, Pause, MapPin, Camera, Sparkles, Film } from 'lucide-react';
import { useLightbox } from './LightboxProvider';
import { isVideoUrl, isPdfUrl, posterFor } from '../lib/media';

const COLOR_MAP = { green: '#22C55E', yellow: '#EAB308', blue: '#3B82F6' };
const COMP_SIGNS = { planned: '?', 'in-progress': '~', completed: '\u2713' };
const COLOR_LABELS = { green: 'Reconstruction', yellow: 'Crucial Enhancement', blue: 'Requested Enhancement' };
const ZONE_LABELS = { outdoor: 'Resort Grounds', indoor: '2nd Floor Plan', 'back-of-house': 'Back of House', rooftop: '4th Floor Rooftop' };

// Order priority for the Virtual Tour:
// 1. Renders (images) — proposed / AI-generated visuals
// 2. Videos (walkthroughs from either the renders OR images list)
// 3. Actual site photos (from images list)
// 4. After / Before legacy snapshots
function getAreaImages(area) {
  // PDFs and other documents are skipped from the Virtual Tour — only
  // images and videos belong in a fullscreen walkthrough.
  const renderItems = (area.renders || []).filter((r) => !isPdfUrl(r)).map((r) => ({
    url: r,
    label: isVideoUrl(r) ? 'Video' : 'Render',
    isVideo: isVideoUrl(r),
    isRender: !isVideoUrl(r),
  }));
  const photoItems = (area.images || []).filter((i) => !isPdfUrl(i)).map((i) => ({
    url: i,
    label: isVideoUrl(i) ? 'Video' : 'Photo',
    isVideo: isVideoUrl(i),
    isRender: false,
  }));

  // Split renders into non-video (stays first) and videos (pushed after renders).
  const rendersOnly = renderItems.filter((x) => !x.isVideo);
  const renderVideos = renderItems.filter((x) => x.isVideo);
  const photoVideos = photoItems.filter((x) => x.isVideo);
  const photosOnly = photoItems.filter((x) => !x.isVideo);

  const ordered = [
    ...rendersOnly,
    ...renderVideos,
    ...photoVideos,
    ...photosOnly,
  ];
  if (area.after_image) ordered.push({ url: area.after_image, label: 'After', isVideo: false, isRender: false });
  if (area.before_image) ordered.push({ url: area.before_image, label: 'Before', isVideo: false, isRender: false });
  return ordered;
}

export default function VirtualTour({ areas, slides, startAreaId, onClose }) {
  const [currentIdx, setCurrentIdx] = useState(0);
  const { open: openLightbox } = useLightbox();
  const [imgIdx, setImgIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [fade, setFade] = useState(true);
  const timerRef = useRef(null);

  // Sort areas by zone order (matching slides) then alphabetically
  const zoneOrder = slides?.length ? slides.map((s) => s.zone) : ['back-of-house', 'indoor', 'rooftop', 'outdoor'];
  const sortedAreas = [...areas]
    .filter((a) => getAreaImages(a).length > 0)
    .sort((a, b) => {
      const ai = zoneOrder.indexOf(a.zone);
      const bi = zoneOrder.indexOf(b.zone);
      if (ai !== bi) return ai - bi;
      return a.name.localeCompare(b.name);
    });

  const area = sortedAreas[currentIdx];
  const images = area ? getAreaImages(area) : [];

  // Initialize starting index from startAreaId
  useEffect(() => {
    if (startAreaId) {
      const idx = sortedAreas.findIndex((a) => a.id === startAreaId);
      if (idx >= 0) setCurrentIdx(idx);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startAreaId]);

  const goTo = useCallback((idx) => {
    setFade(false);
    setTimeout(() => {
      setCurrentIdx(idx);
      setImgIdx(0);
      setFade(true);
    }, 300);
  }, []);

  const next = useCallback(() => {
    if (currentIdx < sortedAreas.length - 1) goTo(currentIdx + 1);
    else goTo(0);
  }, [currentIdx, sortedAreas.length, goTo]);

  const prev = useCallback(() => {
    if (currentIdx > 0) goTo(currentIdx - 1);
    else goTo(sortedAreas.length - 1);
  }, [currentIdx, sortedAreas.length, goTo]);

  // Auto-play
  useEffect(() => {
    if (playing) {
      timerRef.current = setInterval(() => {
        setImgIdx((prev) => {
          const imgs = getAreaImages(sortedAreas[currentIdx]);
          if (prev < imgs.length - 1) return prev + 1;
          // Move to next area
          setTimeout(() => next(), 100);
          return 0;
        });
      }, 4000);
    }
    return () => clearInterval(timerRef.current);
  }, [playing, currentIdx, sortedAreas, next]);

  // Keyboard navigation
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === ' ') { e.preventDefault(); setPlaying((p) => !p); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [next, prev, onClose]);

  if (!area) return null;

  const color = COLOR_MAP[area.color] || '#22C55E';
  const sign = COMP_SIGNS[area.completion] || COMP_SIGNS[area.status] || '?';
  const progress = area.progress_percent || 0;
  const currentImage = images[imgIdx] || images[0];

  return (
    <div data-testid="virtual-tour" className="fixed inset-0 z-[9999] flex flex-col" style={{ backgroundColor: '#0a0a0a' }}>
      {/* Top Bar */}
      <div className="flex items-center justify-between px-4 sm:px-6 py-3" style={{ backgroundColor: '#1A2E2E' }}>
        <div className="flex items-center gap-3">
          <MapPin className="w-4 h-4" style={{ color: '#A3D4D4' }} />
          <span className="text-xs sm:text-sm font-bold text-white" style={{ fontFamily: 'Manrope' }}>Virtual Tour</span>
          <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ backgroundColor: 'rgba(255,255,255,0.1)', color: '#A3D4D4', fontFamily: 'Manrope' }}>
            {currentIdx + 1} / {sortedAreas.length}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setPlaying((p) => !p)} className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-[10px] sm:text-xs font-bold transition-all" style={{ backgroundColor: playing ? '#EF4444' : '#1F4FBE', color: 'white', fontFamily: 'Manrope' }}>
            {playing ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
            {playing ? 'Pause' : 'Auto Play'}
          </button>
          <button onClick={onClose} type="button" aria-label="Close virtual tour" className="w-11 h-11 rounded-full bg-white/15 hover:bg-white/30 text-white flex items-center justify-center hover:scale-110 active:scale-90 transition-transform duration-75 cursor-pointer" style={{ touchAction: 'manipulation' }}>
            <X className="w-5 h-5" strokeWidth={2.5} />
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
        {/* Image Area */}
        <div className="flex-1 relative flex items-center justify-center p-4 sm:p-8">
          {/* Nav arrows */}
          <button onClick={prev} className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-all hover:scale-110 z-10" style={{ backgroundColor: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(4px)' }}>
            <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
          </button>
          <button onClick={next} className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-all hover:scale-110 z-10" style={{ backgroundColor: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(4px)' }}>
            <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
          </button>

          {/* Image / video with transition */}
          <div className={`max-w-full max-h-full transition-all duration-300 ${fade ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
            {currentImage ? (
              <div className="relative">
                {currentImage.isVideo ? (
                  <video
                    key={currentImage.url}
                    src={currentImage.url}
                    poster={posterFor(currentImage.url) || undefined}
                    className="max-h-[60vh] sm:max-h-[70vh] w-auto mx-auto rounded-xl shadow-2xl object-contain bg-black"
                    controls
                    autoPlay
                    playsInline
                    preload="metadata"
                  />
                ) : (
                  <img
                    src={currentImage.url}
                    alt={area.name}
                    className="max-h-[60vh] sm:max-h-[70vh] w-auto mx-auto rounded-xl shadow-2xl object-contain cursor-zoom-in"
                    onClick={() => openLightbox(currentImage.url, images.filter((i) => !i.isVideo).map((i) => i.url))}
                  />
                )}
                <span
                  className="absolute top-3 right-3 px-2.5 py-1 rounded-full text-[10px] font-bold text-white shadow-lg"
                  style={{
                    backgroundColor:
                      currentImage.label === 'Render' ? '#8B5CF6'
                      : currentImage.label === 'Video' ? '#EF4444'
                      : currentImage.label === 'Before' ? '#EF4444'
                      : '#22C55E',
                    fontFamily: 'Manrope',
                  }}
                >
                  {currentImage.label === 'Render' && <Sparkles className="w-3 h-3 inline mr-1" />}
                  {currentImage.label === 'Video' && <Film className="w-3 h-3 inline mr-1" />}
                  {currentImage.label === 'Photo' && <Camera className="w-3 h-3 inline mr-1" />}
                  {currentImage.label}
                </span>
              </div>
            ) : (
              <div className="w-64 h-48 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
                <span className="text-sm text-white/30" style={{ fontFamily: 'Manrope' }}>No images</span>
              </div>
            )}

            {/* Image thumbnails */}
            {images.length > 1 && (
              <div className="flex justify-center gap-2 mt-4 flex-wrap">
                {images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setImgIdx(idx)}
                    className="w-12 h-9 rounded-lg overflow-hidden border-2 transition-all relative"
                    style={{ borderColor: idx === imgIdx ? 'white' : 'transparent', opacity: idx === imgIdx ? 1 : 0.5 }}
                  >
                    <img
                      src={img.isVideo ? (posterFor(img.url) || '') : img.url}
                      alt=""
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.style.backgroundColor = '#1A2E2E'; e.target.style.opacity = '0'; }}
                    />
                    {img.isVideo && (
                      <span className="absolute inset-0 flex items-center justify-center">
                        <Film className="w-3 h-3 text-white drop-shadow-lg" />
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Info Sidebar */}
        <div className="w-full lg:w-80 xl:w-96 overflow-y-auto p-4 sm:p-6" style={{ backgroundColor: '#141414' }}>
          {/* Area header */}
          <div className="mb-4">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold text-white" style={{ backgroundColor: color }}>
                {COLOR_LABELS[area.color] || 'Reconstruction'}
              </span>
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold text-white" style={{ backgroundColor: 'rgba(255,255,255,0.15)' }}>
                {sign}
              </span>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: 'rgba(255,255,255,0.1)', color: '#A3D4D4', fontFamily: 'Manrope' }}>
                {ZONE_LABELS[area.zone] || area.zone}
              </span>
            </div>

            <h2 className="text-xl sm:text-2xl font-medium text-white mb-1" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              {area.name}
            </h2>

            {/* Progress bar */}
            <div className="flex items-center gap-3 mb-3">
              <div className="flex-1 h-2 rounded-full" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}>
                <div className="h-full rounded-full transition-all duration-500" style={{ width: `${progress}%`, backgroundColor: color }} />
              </div>
              <span className="text-xs font-bold" style={{ color, fontFamily: 'Manrope' }}>{progress}%</span>
            </div>

            <p className="text-sm leading-relaxed" style={{ fontFamily: 'Manrope', color: 'rgba(255,255,255,0.6)' }}>
              {area.description}
            </p>
          </div>

          {/* Enhancements */}
          {area.enhancements?.length > 0 && (
            <div className="mb-4">
              <h3 className="text-xs font-bold mb-2 tracking-wider uppercase" style={{ color: '#A3D4D4', fontFamily: 'Manrope' }}>Enhancements</h3>
              <div className="space-y-1.5">
                {area.enhancements.map((enh, idx) => (
                  <div key={idx} className="p-2.5 rounded-lg" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
                    <p className="text-xs font-bold text-white" style={{ fontFamily: 'Manrope' }}>{enh.title}</p>
                    <p className="text-[11px]" style={{ fontFamily: 'Manrope', color: 'rgba(255,255,255,0.4)' }}>{enh.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Area List / Mini Map */}
          <div>
            <h3 className="text-xs font-bold mb-2 tracking-wider uppercase" style={{ color: '#A3D4D4', fontFamily: 'Manrope' }}>All Areas</h3>
            <div className="space-y-1 max-h-48 overflow-y-auto pr-1">
              {sortedAreas.map((a, idx) => {
                const c = COLOR_MAP[a.color] || '#22C55E';
                return (
                  <button
                    key={a.id}
                    onClick={() => goTo(idx)}
                    className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-left transition-all"
                    style={{ backgroundColor: idx === currentIdx ? 'rgba(255,255,255,0.1)' : 'transparent' }}
                  >
                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: c }} />
                    <span className="text-[11px] truncate" style={{ fontFamily: 'Manrope', color: idx === currentIdx ? 'white' : 'rgba(255,255,255,0.4)' }}>
                      {a.name}
                    </span>
                    <span className="ml-auto text-[9px] flex-shrink-0" style={{ color: 'rgba(255,255,255,0.3)' }}>
                      {a.progress_percent || 0}%
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom progress indicator */}
      <div className="h-1" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
        <div className="h-full transition-all duration-500" style={{ width: `${((currentIdx + 1) / sortedAreas.length) * 100}%`, backgroundColor: color }} />
      </div>
    </div>
  );
}
